import * as XLSX from "xlsx";
import { getQuoteLineUnitPrice } from "@/utils/quotePricing";
import {
  type AllQuoteFieldsFragment as QuoteType,
  type QuoteLineItemFieldsFragment as QuoteLineItemType
} from "gql/api";
import {
  getSpyGlassDate,
  getLineItemTypeLabelCamel
} from "@/utils/quoteHelpers";

/**
 * Exports a quote to an Excel workbook with two sheets:
 *  - "Quote Summary": header/sales info (customer, well, pricing, rep, job details)
 *  - "Line Items": the quote design list (material number, description, cost, margin, unit price, qty, total)
 */
export const exportQuoteToExcel = (quote: QuoteType): void => {
  if (!quote) return;

  const workbook = XLSX.utils.book_new();

  // --- Sheet 1: Quote Summary ---
  const summaryData = buildSummarySheet(quote);
  const summarySheet = XLSX.utils.json_to_sheet(summaryData, {
    skipHeader: true
  });

  // Widen columns for readability
  summarySheet["!cols"] = [
    { wch: 28 }, // Field
    { wch: 50 } // Value
  ];

  XLSX.utils.book_append_sheet(workbook, summarySheet, "Quote Summary");

  // --- Sheet 2: Line Items ---
  const lineItemsData = buildLineItemsSheet(quote);
  const lineItemsSheet = XLSX.utils.json_to_sheet(lineItemsData);

  for (let row = 2; row <= lineItemsData.length + 1; row += 1) {
    for (const column of ["E", "H", "J"]) {
      const cell = lineItemsSheet[`${column}${row}`];
      if (cell) cell.z = "#,##0.00";
    }
  }

  lineItemsSheet["!cols"] = [
    { wch: 6 }, // #
    { wch: 16 }, // Type
    { wch: 18 }, // Material Number
    { wch: 50 }, // Description
    { wch: 14 }, // Cost
    { wch: 14 }, // Pricing Date
    { wch: 10 }, // Margin %
    { wch: 14 }, // Unit Price
    { wch: 8 }, // Qty
    { wch: 14 }, // Total
    { wch: 30 } // Note
  ];

  XLSX.utils.book_append_sheet(workbook, lineItemsSheet, "Line Items");

  // --- Download ---
  const quoteName = quote.name || "Quote";
  const proposalNumber = quote.sk_proposal_number || "";
  const fileName = sanitizeFileName(
    `${quoteName}${proposalNumber ? ` - ${proposalNumber}` : ""}.xlsx`
  );
  XLSX.writeFile(workbook, fileName);
};

/**
 * Build the Quote Summary sheet data as key/value pairs.
 */
function buildSummarySheet(
  quote: QuoteType
): Array<Record<string, string | number>> {
  const rows: Array<Record<string, string | number>> = [];

  const addRow = (field: string, value: string | number | null | undefined) => {
    rows.push({ Field: field, Value: value ?? "" });
  };

  const addBlankRow = () => {
    rows.push({ Field: "", Value: "" });
  };

  // Quote Identity
  addRow("Field", "Value"); // header row
  addBlankRow();
  addRow("QUOTE DETAILS", "");
  addRow("Quote Name", quote.name);
  addRow("Proposal Number", quote.sk_proposal_number);
  addRow("Version", quote.version_number);
  addRow("Status", quote.status);
  addRow("Source", quote.source);
  addRow("Currency", quote.currency);
  addRow(
    "Pricing Date",
    quote.pricing_date ? String(getSpyGlassDate(quote.pricing_date)) : ""
  );
  addRow(
    "Proposed Start Date",
    quote.proposed_start_date
      ? String(getSpyGlassDate(quote.proposed_start_date))
      : ""
  );

  addBlankRow();
  addRow("CUSTOMER DETAILS", "");
  addRow("Customer Name", quote.erp_customer_name);
  addRow("Customer Number", quote.erp_customer_number);
  addRow("NWA", quote.erp_nwa_name);
  addRow("Contract Number", quote.erp_contract_number);

  addBlankRow();
  addRow("WELL DETAILS", "");
  addRow("Well Name", quote.erp_well_name);
  addRow("Well UWI", quote.erp_well_uwi);
  addRow("Well Country", quote.erp_well_country);
  addRow("Well Region", quote.erp_well_region);
  addRow("Address Name", quote.erp_address_name);
  addRow("Full Address", quote.erp_full_address);

  addBlankRow();
  addRow("PLANT & PRICING", "");
  addRow("Plant / Sales District", quote.erp_plant_name);
  addRow("Plant ID", quote.erp_plant_id);
  addRow("Price List", quote.erp_price_list_name);
  addRow("Price List Number", quote.erp_price_list_number);

  addBlankRow();
  addRow("REP DETAILS", "");
  addRow("HESI Rep Name", quote.customer_rep_name);
  addRow("HESI Rep Email", quote.customer_rep_email);
  addRow("HESI Rep Phone", quote.customer_rep_phone_number);

  addBlankRow();
  addRow("JOB DETAILS", "");
  addRow("Job Type", quote.sk_job_type);
  addRow("Job Number", quote.sk_job_number);
  addRow("Planning Order", quote.sk_planning_order);

  if (quote.comments) {
    addBlankRow();
    addRow("NOTES", "");
    addRow("Comments", quote.comments);
  }

  if (quote.directions) {
    addRow("Directions", quote.directions);
  }

  if (quote.note) {
    addRow("Note", quote.note);
  }

  // Totals summary
  addBlankRow();
  addRow("TOTALS", "");
  const lineItems = quote.quote_line_items || [];
  const totalValue = lineItems.reduce((sum, item) => {
    const price = getQuoteLineUnitPrice(item);
    const qty = Number(item.quantity) || 0;
    const isFree = item.tax_type === "free";
    return sum + (isFree ? 0 : price * qty);
  }, 0);
  addRow("Total Line Items", String(lineItems.length));
  addRow(
    `Total Value${quote.currency ? ` (${quote.currency})` : ""}`,
    totalValue
  );

  return rows;
}

/**
 * Build the Line Items sheet data.
 */
export function buildLineItemsSheet(
  quote: QuoteType
): Array<Record<string, string | number>> {
  const lineItems = quote.quote_line_items || [];
  return lineItems.map((item: QuoteLineItemType, index: number) => {
    const unitPrice = getQuoteLineUnitPrice(item);
    const qty = Number(item.quantity) || 0;
    const isFree = item.tax_type === "free";
    const total = isFree ? 0 : unitPrice * qty;

    return {
      "#": (index + 1) * 10,
      Type: item.line_item_type
        ? getLineItemTypeLabelCamel(item.line_item_type)
        : "",
      "Material Number": item.erp_material_number || "",
      Description: item.erp_material_long_description || "",
      Cost: Number(item.erp_price) || 0,
      "Pricing Date": item.pricing_date
        ? String(getSpyGlassDate(item.pricing_date))
        : "",
      "Margin %": item.margin != null ? Number(item.margin) : "",
      "Unit Price": unitPrice,
      Qty: qty,
      Total: total,
      Note: item.note || ""
    };
  });
}

/**
 * Sanitize a string for use as a file name.
 */
function sanitizeFileName(name: string): string {
  return name.replace(/[<>:"/\\|?*]/g, "_");
}
